import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { AbstractControlOptions, FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ConfirmedValidator } from '@authentication/utils';
import { SnackbarService } from '@core/services/snackbar.service';
import { UserProfileService } from '@core/services/user-profile/user-profile.service';
import { CustomSnackbarType } from '@shared/component/custom-snackbar/custom-snackbar.model';
import { RegexPatterns } from '@shared/validators/regex-patterns.enum';
import { BehaviorSubject, EMPTY, Subject, takeUntil, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
	selector: 'sf-change-password-modal',
	templateUrl: './change-password-modal.component.html',
	styleUrls: ['./change-password-modal.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangePasswordModalComponent implements OnDestroy {
	public showCurrentPasswordError: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public buttonDisabled: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public changePasswordFrom = this.fb.group(
		{
			currentPassword: new FormControl<string>(null, [
				Validators.required,
				Validators.maxLength(50),
				Validators.pattern(RegexPatterns.PASSWORD),
			]),
			newPassword: new FormControl<string>(null, [
				Validators.required,
				Validators.maxLength(50),
				Validators.pattern(RegexPatterns.PASSWORD),
			]),
			newPasswordConfirmation: new FormControl<string>(null, [Validators.required, Validators.maxLength(50)]),
		},
		{ validators: [ConfirmedValidator('newPassword', 'newPasswordConfirmation')] } as AbstractControlOptions
	);
	private destroy: Subject<void> = new Subject();

	public get currentPasswordControl(): FormControl<string> {
		return this.changePasswordFrom.controls.currentPassword;
	}
	public get newPasswordControl(): FormControl<string> {
		return this.changePasswordFrom.controls.newPassword;
	}
	public get newPasswordConfirmationControl(): FormControl<string> {
		return this.changePasswordFrom.controls.newPasswordConfirmation;
	}

	constructor(
		private readonly fb: FormBuilder,
		private readonly userProfileService: UserProfileService,
		private readonly snackBarService: SnackbarService,
		private readonly dialogRef: MatDialogRef<ChangePasswordModalComponent>
	) {}

	public ngOnDestroy(): void {
		this.destroy.next();
		this.destroy.complete();
	}

	public onPasswordBlur(): void {
		if (this.newPasswordControl.dirty) {
			this.newPasswordControl.updateValueAndValidity();
			this.newPasswordControl.markAsTouched();
			this.newPasswordConfirmationControl.updateValueAndValidity();
		}
	}

	public onChangePasswordClick(): void {
		this.buttonDisabled.next(true);
		this.userProfileService
			.changePassword(this.changePasswordFrom.getRawValue())
			.pipe(
				catchError((error) => {
					if (error.error.currentPassword) {
						this.showCurrentPasswordError.next(true);
						this.currentPasswordControl.setValue('');
					}
					this.buttonDisabled.next(false);
					this.snackBarService.openSnackbar({
						message: 'sf.shared_component.my_account.change_password.notifications.error',
						type: CustomSnackbarType.Error,
					});
					return EMPTY;
				}),
				tap(() => {
					this.dialogRef.close();
					this.snackBarService.openSnackbar({
						message: 'sf.shared_component.my_account.change_password.notifications.success',
						type: CustomSnackbarType.Success,
					});
				}),
				takeUntil(this.destroy)
			)
			.subscribe();
	}
}
